import { siteConfig, type Locale } from "./config";

type LinkText = {
  projects: string;
  contact: string;
  privacy: string;
  terms: string;
  language: string;
  comingSoon: string;
  learnMore: string;
  backHome: string;
  followOnX: string;
};

type HomeContent = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  lead: string;
  projectSectionTitle: string;
  projectSectionLead: string;
  principlesTitle: string;
  principlesLead: string;
  principles: Array<{ title: string; text: string }>;
  dreamJarCard: {
    label: string;
    title: string;
    text: string;
  };
};

type DreamJarContent = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  lead: string;
  status: string;
  appStoreSoon: string;
  proofPoints: string[];
  featuresTitle: string;
  featuresLead: string;
  features: Array<{ title: string; text: string }>;
  privacyTitle: string;
  privacyLead: string;
  proTitle: string;
  proLead: string;
  ctaTitle: string;
  ctaLead: string;
  phone: {
    child: string;
    plan: string;
    activeDreams: string;
    coins: string;
    journal: string;
    dreamName: string;
    progress: string;
    note: string;
  };
};

type LegalContent = {
  privacyMetaTitle: string;
  privacyMetaDescription: string;
  termsMetaTitle: string;
  termsMetaDescription: string;
  updated: string;
  privacyTitle: string;
  privacyLead: string;
  privacySections: Array<{ title: string; body: string[] }>;
  termsTitle: string;
  termsLead: string;
  termsSections: Array<{ title: string; body: string[] }>;
};

export type LocaleContent = {
  nav: LinkText;
  home: HomeContent;
  dreamjar: DreamJarContent;
  legal: LegalContent;
  notFound: {
    title: string;
    text: string;
  };
};

export const content: Record<Locale, LocaleContent> = {
  en: {
    nav: {
      projects: "Projects",
      contact: "Contact on X",
      privacy: "Privacy",
      terms: "Terms",
      language: "Language",
      comingSoon: "Coming soon",
      learnMore: "Learn more",
      backHome: "Back to MuzoLab",
      followOnX: "Follow updates on X"
    },
    home: {
      metaTitle: "MuzoLab | Small independent product studio",
      metaDescription:
        "MuzoLab is a small independent studio building thoughtful, complete digital products. Explore DreamJar and future small-but-beautiful projects.",
      eyebrow: "Independent product studio",
      headline: "Small, thoughtful, complete products.",
      lead:
        "MuzoLab builds focused digital products with a calm interface, careful privacy posture, and enough craft to feel finished.",
      projectSectionTitle: "Projects",
      projectSectionLead: "A growing collection of small products, each with its own landing page and long-lived home.",
      principlesTitle: "How MuzoLab builds",
      principlesLead: "The studio favors clarity, restraint, privacy, and products that solve one real job well.",
      principles: [
        {
          title: "Small by design",
          text: "Each product starts with a narrow promise, then earns every feature through usefulness."
        },
        {
          title: "Privacy-aware defaults",
          text: "Personal data should stay local or in user-owned systems whenever the product allows it."
        },
        {
          title: "Made to last",
          text: "MuzoLab prefers durable product surfaces, understandable language, and maintainable engineering."
        }
      ],
      dreamJarCard: {
        label: "iOS app",
        title: "DreamJar",
        text: "A parent-child dream jar and growth journal for turning everyday effort into visible progress."
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | Parent-child dream jars and growth journal",
      metaDescription:
        "DreamJar is an upcoming iOS app from MuzoLab for family dream jars, coin ledgers, growth journals, iCloud sync, and private local-first records.",
      eyebrow: "Coming soon for iOS",
      headline: "A warmer way for families to save for dreams.",
      lead:
        "DreamJar helps parents and children turn goals into coin progress, notes, photos, and memories without needing a custom account or server.",
      status: "Coming soon for iOS",
      appStoreSoon: "App Store link coming soon",
      proofPoints: ["Local-first records", "Private iCloud sync", "Growth journal", "Apple in-app purchase"],
      featuresTitle: "What DreamJar does",
      featuresLead: "A simple loop for goals, effort, memories, and reviews.",
      features: [
        {
          title: "Dream jars with coin progress",
          text: "Create dreams, set target coins, deposit or withdraw coins, and see when a dream is ready to fulfill."
        },
        {
          title: "Growth journal with photos",
          text: "Capture small family moments in a timeline or calendar, with up to three photos per entry."
        },
        {
          title: "Fulfillment memories",
          text: "When a dream is fulfilled, keep the journey, parent notes, and photos in a dedicated archive."
        },
        {
          title: "Private by architecture",
          text: "Records are stored on device and sync through the user’s private iCloud account when available."
        }
      ],
      privacyTitle: "No custom account system",
      privacyLead:
        "DreamJar is designed around local storage, user-controlled iCloud sync, and Apple’s purchase system instead of a custom backend.",
      proTitle: "Free first, Pro when families grow",
      proLead:
        "The free plan supports one child and three active dream jars. Pro unlocks multiple children, unlimited active jars, yearly reviews, and growth-review sharing.",
      ctaTitle: "Follow DreamJar",
      ctaLead: "The iOS app is being prepared. Follow MuzoLab on X for release updates and product notes.",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "Active dreams",
        coins: "Coins",
        journal: "Journal",
        dreamName: "Space telescope",
        progress: "145 / 200 coins",
        note: "Reading time +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJar Privacy Policy | MuzoLab",
      privacyMetaDescription:
        "DreamJar privacy policy covering local storage, private iCloud sync, photos, notifications, and Apple in-app purchases.",
      termsMetaTitle: "DreamJar Terms of Service | MuzoLab",
      termsMetaDescription:
        "DreamJar terms covering app use, family records, coin rules, Pro access, iCloud sync, and Apple in-app purchases.",
      updated: `Last updated: ${siteConfig.buildDate}`,
      privacyTitle: "DreamJar Privacy Policy",
      privacyLead:
        "DreamJar is designed as a local-first family app. This policy explains what the app stores and how sync and purchases work.",
      privacySections: [
        {
          title: "Data stored by the app",
          body: [
            "DreamJar stores child profiles, dream jars, coin transactions, journal entries, photos, fulfillment notes, app settings, and purchase entitlement status.",
            "This data is used to provide the product experience inside DreamJar."
          ]
        },
        {
          title: "Local-first and iCloud sync",
          body: [
            "DreamJar stores records on the user’s device. When iCloud is available, records may sync through the user’s private iCloud account.",
            "MuzoLab does not operate a custom DreamJar account system or custom DreamJar sync server."
          ]
        },
        {
          title: "Photos and media",
          body: [
            "Photos selected for journals, avatars, covers, and fulfillment memories are stored as app data on device and may sync through iCloud if iCloud sync is enabled by the system.",
            "DreamJar does not use these photos for advertising."
          ]
        },
        {
          title: "Purchases and notifications",
          body: [
            "DreamJar Pro is handled through Apple in-app purchase. Apple processes purchase and subscription transactions.",
            "Local notifications are used for dream progress, target dates, check-ins, and reviews when the user allows notifications."
          ]
        },
        {
          title: "Contact",
          body: [`Questions can be sent to MuzoLab on X at ${siteConfig.xHandle}.`]
        }
      ],
      termsTitle: "DreamJar Terms of Service",
      termsLead:
        "These terms describe the expected use of DreamJar and the relationship between the free and Pro features.",
      termsSections: [
        {
          title: "Use of DreamJar",
          body: [
            "DreamJar is a family productivity and memory app. It is not a bank, wallet, payment service, or financial account.",
            "Coins inside DreamJar are symbolic progress units and have no cash value."
          ]
        },
        {
          title: "Free and Pro features",
          body: [
            "Free users can create one child profile and up to three active dream jars.",
            "DreamJar Pro unlocks multiple children, unlimited active dream jars, yearly reviews, and growth-review sharing. Existing records are not deleted if Pro access ends."
          ]
        },
        {
          title: "iCloud and device responsibility",
          body: [
            "DreamJar uses on-device storage and may sync through the user’s private iCloud account. Availability depends on Apple services, device settings, storage, and network status.",
            "Users are responsible for keeping their Apple ID, devices, and iCloud settings secure."
          ]
        },
        {
          title: "Purchases",
          body: [
            "Subscriptions and lifetime unlocks are provided through Apple in-app purchase. Apple’s terms apply to payment, renewal, cancellation, refund, and subscription management.",
            "Product prices and availability may vary by region and are shown by the App Store."
          ]
        },
        {
          title: "Contact",
          body: [`For support or questions, contact MuzoLab on X at ${siteConfig.xHandle}.`]
        }
      ]
    },
    notFound: {
      title: "Page not found",
      text: "This page does not exist yet."
    }
  },
  ja: {
    nav: {
      projects: "プロジェクト",
      contact: "Xで連絡",
      privacy: "プライバシー",
      terms: "利用規約",
      language: "言語",
      comingSoon: "近日公開",
      learnMore: "詳しく見る",
      backHome: "MuzoLabへ戻る",
      followOnX: "Xで最新情報を見る"
    },
    home: {
      metaTitle: "MuzoLab | 小さな独立プロダクトスタジオ",
      metaDescription:
        "MuzoLabは、思慮深く完成度のあるデジタルプロダクトをつくる小さな独立スタジオです。DreamJarと今後の小さく美しいプロジェクトを紹介します。",
      eyebrow: "独立プロダクトスタジオ",
      headline: "小さく、丁寧で、完成されたプロダクト。",
      lead:
        "MuzoLabは、落ち着いたUI、慎重なプライバシー設計、きちんと使い切れる品質を備えたプロダクトをつくります。",
      projectSectionTitle: "プロジェクト",
      projectSectionLead: "小さなプロダクトを少しずつ増やし、それぞれに独立したランディングページを用意します。",
      principlesTitle: "MuzoLabのつくり方",
      principlesLead: "明快さ、節度、プライバシー、そしてひとつの仕事をよく解くことを重視します。",
      principles: [
        { title: "小さく設計する", text: "狭い約束から始め、役に立つ機能だけを増やします。" },
        { title: "プライバシーを前提に", text: "可能な限り、個人データはローカルまたはユーザー所有の仕組みに置きます。" },
        { title: "長く使える形に", text: "耐久性のある画面、わかりやすい言葉、保守しやすい実装を選びます。" }
      ],
      dreamJarCard: {
        label: "iOSアプリ",
        title: "DreamJar",
        text: "毎日の努力を見える進捗に変える、親子の夢の貯金箱と成長日記です。"
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | 親子の夢の貯金箱と成長日記",
      metaDescription:
        "DreamJarはMuzoLabの近日公開予定iOSアプリです。夢の貯金箱、コイン記録、成長日記、iCloud同期、ローカルファーストの記録を提供します。",
      eyebrow: "iOS版近日公開",
      headline: "家族の夢を、あたたかく貯めていく。",
      lead:
        "DreamJarは、親子の目標をコインの進捗、メモ、写真、思い出として残せるアプリです。独自アカウントや専用サーバーは不要です。",
      status: "iOS版近日公開",
      appStoreSoon: "App Storeリンクは準備中",
      proofPoints: ["ローカルファースト", "プライベートiCloud同期", "成長日記", "Appleアプリ内課金"],
      featuresTitle: "DreamJarでできること",
      featuresLead: "目標、努力、思い出、振り返りをシンプルな流れにまとめます。",
      features: [
        { title: "コインで進む夢のジャー", text: "夢を作り、目標コインを決め、コインを追加・調整して達成を確認できます。" },
        { title: "写真つき成長日記", text: "小さな家族の出来事をタイムラインやカレンダーに残せます。" },
        { title: "達成の思い出", text: "叶えた夢は、過程、親のメモ、写真と一緒にアーカイブされます。" },
        { title: "構造からプライベート", text: "記録は端末に保存され、利用可能な場合はユーザーのプライベートiCloudで同期されます。" }
      ],
      privacyTitle: "独自アカウントなし",
      privacyLead: "DreamJarは、ローカル保存、ユーザー管理のiCloud同期、Appleの購入システムを中心に設計されています。",
      proTitle: "まず無料、家族が広がったらPro",
      proLead: "無料版は子ども1人と進行中の夢3件まで。Proで複数の子ども、無制限の夢、年間レビュー、共有機能を解放します。",
      ctaTitle: "DreamJarをフォロー",
      ctaLead: "iOSアプリを準備中です。リリース情報はMuzoLabのXでお知らせします。",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "進行中の夢",
        coins: "コイン",
        journal: "日記",
        dreamName: "宇宙望遠鏡",
        progress: "145 / 200 コイン",
        note: "読書時間 +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJarプライバシーポリシー | MuzoLab",
      privacyMetaDescription: "DreamJarのローカル保存、iCloud同期、写真、通知、Appleアプリ内課金に関するプライバシーポリシーです。",
      termsMetaTitle: "DreamJar利用規約 | MuzoLab",
      termsMetaDescription: "DreamJarの利用、家族の記録、コインルール、Proアクセス、iCloud同期、Appleアプリ内課金に関する規約です。",
      updated: `最終更新日: ${siteConfig.buildDate}`,
      privacyTitle: "DreamJarプライバシーポリシー",
      privacyLead: "DreamJarはローカルファーストの家族向けアプリとして設計されています。",
      privacySections: [
        { title: "保存されるデータ", body: ["子どもプロフィール、夢のジャー、コイン記録、日記、写真、達成メモ、設定、購入権限の状態を保存します。"] },
        { title: "ローカルとiCloud同期", body: ["記録は端末に保存され、iCloudが利用できる場合はユーザーのプライベートiCloudアカウントで同期されることがあります。MuzoLabは独自のDreamJarアカウントや同期サーバーを運営しません。"] },
        { title: "写真とメディア", body: ["選択した写真はアプリデータとして保存され、システム設定に応じてiCloudで同期されることがあります。広告目的には使用しません。"] },
        { title: "購入と通知", body: ["DreamJar ProはAppleアプリ内課金で処理されます。通知を許可した場合、進捗や期日、振り返りの通知にローカル通知を使います。"] },
        { title: "連絡先", body: [`質問はXの${siteConfig.xHandle}までお送りください。`] }
      ],
      termsTitle: "DreamJar利用規約",
      termsLead: "DreamJarの利用と無料機能・Pro機能の関係を説明します。",
      termsSections: [
        { title: "DreamJarの利用", body: ["DreamJarは家族の生産性と思い出のためのアプリです。銀行、ウォレット、決済サービスではありません。アプリ内のコインに現金価値はありません。"] },
        { title: "無料版とPro", body: ["無料版では子ども1人、進行中の夢3件まで利用できます。Proは複数の子ども、無制限の夢、年間レビュー、共有機能を解放します。Pro終了後も既存記録は削除されません。"] },
        { title: "iCloudと端末", body: ["同期の可用性はAppleサービス、端末設定、容量、ネットワークに依存します。Apple IDと端末の安全管理はユーザーの責任です。"] },
        { title: "購入", body: ["サブスクリプションと買い切り解除はAppleアプリ内課金で提供されます。支払い、更新、キャンセル、返金にはAppleの規約が適用されます。"] },
        { title: "連絡先", body: [`サポートはXの${siteConfig.xHandle}まで。`] }
      ]
    },
    notFound: { title: "ページが見つかりません", text: "このページはまだ存在しません。" }
  },
  ko: {
    nav: {
      projects: "프로젝트",
      contact: "X로 문의",
      privacy: "개인정보",
      terms: "약관",
      language: "언어",
      comingSoon: "출시 예정",
      learnMore: "자세히 보기",
      backHome: "MuzoLab으로 돌아가기",
      followOnX: "X에서 소식 보기"
    },
    home: {
      metaTitle: "MuzoLab | 작은 독립 제품 스튜디오",
      metaDescription:
        "MuzoLab은 사려 깊고 완성도 있는 디지털 제품을 만드는 작은 독립 스튜디오입니다. DreamJar와 앞으로의 작고 아름다운 프로젝트를 소개합니다.",
      eyebrow: "독립 제품 스튜디오",
      headline: "작고, 사려 깊고, 완성된 제품.",
      lead: "MuzoLab은 차분한 인터페이스, 신중한 개인정보 설계, 완성감 있는 작은 디지털 제품을 만듭니다.",
      projectSectionTitle: "프로젝트",
      projectSectionLead: "각 제품은 독립적인 랜딩 페이지와 오래 유지되는 집을 갖습니다.",
      principlesTitle: "MuzoLab의 원칙",
      principlesLead: "명확함, 절제, 개인정보 보호, 하나의 일을 잘 해결하는 제품을 우선합니다.",
      principles: [
        { title: "작게 설계", text: "좁은 약속에서 시작해 실제로 유용한 기능만 더합니다." },
        { title: "개인정보 우선", text: "가능하면 개인 데이터는 로컬 또는 사용자가 소유한 시스템에 둡니다." },
        { title: "오래 쓰는 제품", text: "견고한 화면, 이해하기 쉬운 문구, 유지보수 가능한 구현을 선택합니다." }
      ],
      dreamJarCard: {
        label: "iOS 앱",
        title: "DreamJar",
        text: "일상의 노력을 눈에 보이는 진척으로 바꾸는 부모와 아이의 꿈 저금통 및 성장 일기입니다."
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | 부모와 아이의 꿈 저금통과 성장 일기",
      metaDescription:
        "DreamJar는 가족 꿈 저금통, 코인 기록, 성장 일기, iCloud 동기화, 로컬 우선 기록을 위한 MuzoLab의 iOS 앱입니다.",
      eyebrow: "iOS 출시 예정",
      headline: "가족의 꿈을 더 따뜻하게 모아갑니다.",
      lead: "DreamJar는 목표를 코인 진척, 메모, 사진, 추억으로 남기도록 돕습니다. 별도 계정이나 서버가 필요 없습니다.",
      status: "iOS 출시 예정",
      appStoreSoon: "App Store 링크 준비 중",
      proofPoints: ["로컬 우선 기록", "개인 iCloud 동기화", "성장 일기", "Apple 앱 내 구입"],
      featuresTitle: "DreamJar 기능",
      featuresLead: "목표, 노력, 추억, 돌아보기를 단순한 흐름으로 연결합니다.",
      features: [
        { title: "코인으로 채우는 꿈 저금통", text: "꿈을 만들고 목표 코인을 정한 뒤 입금과 차감으로 달성 시점을 확인합니다." },
        { title: "사진이 있는 성장 일기", text: "가족의 작은 순간을 타임라인이나 캘린더에 기록합니다." },
        { title: "실현한 꿈의 추억", text: "달성한 꿈은 과정, 부모 메모, 사진과 함께 보관됩니다." },
        { title: "구조적으로 사적인 기록", text: "기록은 기기에 저장되며 가능할 때 사용자의 개인 iCloud 계정으로 동기화됩니다." }
      ],
      privacyTitle: "별도 계정 없음",
      privacyLead: "DreamJar는 로컬 저장, 사용자 iCloud 동기화, Apple 구매 시스템을 중심으로 설계되었습니다.",
      proTitle: "무료로 시작하고 필요하면 Pro",
      proLead: "무료 플랜은 아이 1명과 진행 중 꿈 3개를 지원합니다. Pro는 여러 아이, 무제한 꿈, 연간 리뷰, 공유를 엽니다.",
      ctaTitle: "DreamJar 소식 받기",
      ctaLead: "iOS 앱을 준비 중입니다. 출시 소식은 MuzoLab X에서 확인하세요.",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "진행 중 꿈",
        coins: "코인",
        journal: "일기",
        dreamName: "우주 망원경",
        progress: "145 / 200 코인",
        note: "독서 시간 +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJar 개인정보 처리방침 | MuzoLab",
      privacyMetaDescription: "DreamJar의 로컬 저장, 개인 iCloud 동기화, 사진, 알림, Apple 앱 내 구입에 관한 개인정보 처리방침입니다.",
      termsMetaTitle: "DreamJar 이용약관 | MuzoLab",
      termsMetaDescription: "DreamJar 사용, 가족 기록, 코인 규칙, Pro 접근, iCloud 동기화, Apple 앱 내 구입에 관한 약관입니다.",
      updated: `최종 업데이트: ${siteConfig.buildDate}`,
      privacyTitle: "DreamJar 개인정보 처리방침",
      privacyLead: "DreamJar는 로컬 우선 가족 앱으로 설계되었습니다.",
      privacySections: [
        { title: "앱에 저장되는 데이터", body: ["아이 프로필, 꿈 저금통, 코인 거래, 일기, 사진, 실현 메모, 설정, 구매 권한 상태를 저장합니다."] },
        { title: "로컬 우선 및 iCloud 동기화", body: ["기록은 기기에 저장되며 iCloud가 가능하면 사용자의 개인 iCloud 계정으로 동기화될 수 있습니다. MuzoLab은 별도 DreamJar 계정이나 동기화 서버를 운영하지 않습니다."] },
        { title: "사진과 미디어", body: ["선택한 사진은 앱 데이터로 저장되며 시스템 설정에 따라 iCloud로 동기화될 수 있습니다. 광고에 사용하지 않습니다."] },
        { title: "구매와 알림", body: ["DreamJar Pro는 Apple 앱 내 구입으로 처리됩니다. 사용자가 허용하면 진행, 목표일, 체크인, 리뷰 알림에 로컬 알림을 사용합니다."] },
        { title: "문의", body: [`질문은 X의 ${siteConfig.xHandle}로 보내주세요.`] }
      ],
      termsTitle: "DreamJar 이용약관",
      termsLead: "DreamJar 사용과 무료 및 Pro 기능의 관계를 설명합니다.",
      termsSections: [
        { title: "DreamJar 사용", body: ["DreamJar는 가족 생산성과 추억을 위한 앱입니다. 은행, 지갑, 결제 서비스가 아닙니다. 코인은 상징적 진척 단위이며 현금 가치가 없습니다."] },
        { title: "무료 및 Pro 기능", body: ["무료 사용자는 아이 1명과 진행 중 꿈 3개까지 만들 수 있습니다. Pro는 여러 아이, 무제한 꿈, 연간 리뷰, 공유 기능을 제공합니다. Pro가 끝나도 기존 기록은 삭제되지 않습니다."] },
        { title: "iCloud와 기기 책임", body: ["동기화는 Apple 서비스, 기기 설정, 저장 공간, 네트워크에 따라 달라집니다. Apple ID와 기기 보안은 사용자 책임입니다."] },
        { title: "구매", body: ["구독과 평생 잠금 해제는 Apple 앱 내 구입으로 제공됩니다. 결제, 갱신, 취소, 환불에는 Apple 약관이 적용됩니다."] },
        { title: "문의", body: [`지원은 X의 ${siteConfig.xHandle}로 연락하세요.`] }
      ]
    },
    notFound: { title: "페이지를 찾을 수 없습니다", text: "이 페이지는 아직 없습니다." }
  },
  "zh-CN": {
    nav: {
      projects: "项目",
      contact: "在 X 联系",
      privacy: "隐私",
      terms: "条款",
      language: "语言",
      comingSoon: "即将上线",
      learnMore: "了解更多",
      backHome: "返回 MuzoLab",
      followOnX: "在 X 关注更新"
    },
    home: {
      metaTitle: "MuzoLab | 小而美的独立产品工作室",
      metaDescription: "MuzoLab 是一个小而美的独立工作室，专注打造克制、完整、长期可用的数字产品。了解 DreamJar 和未来项目。",
      eyebrow: "独立产品工作室",
      headline: "小而美，克制且完整。",
      lead: "MuzoLab 打造聚焦的数字产品：界面安静、隐私边界清晰、功能不贪多，但每一处都尽量做完整。",
      projectSectionTitle: "项目",
      projectSectionLead: "每个产品都有自己的 landing page，也有一个可以长期维护的家。",
      principlesTitle: "MuzoLab 的产品原则",
      principlesLead: "更重视清晰、克制、隐私和把一个真实问题解决好。",
      principles: [
        { title: "从小处开始", text: "每个产品先承诺一件具体的事，再让真正有用的功能留下来。" },
        { title: "默认尊重隐私", text: "只要产品允许，个人数据就应尽量留在本地或用户自己拥有的系统里。" },
        { title: "面向长期使用", text: "选择耐看的界面、清楚的语言和可维护的工程实现。" }
      ],
      dreamJarCard: {
        label: "iOS 应用",
        title: "DreamJar",
        text: "一个亲子梦想罐与成长日记，把每天的小努力变成看得见的进度。"
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | 亲子梦想罐与成长日记",
      metaDescription: "DreamJar 是 MuzoLab 即将推出的 iOS 应用，支持亲子梦想罐、金币记录、成长日记、iCloud 私有同步和本地优先记录。",
      eyebrow: "iOS 即将上线",
      headline: "让一家人的梦想，被温柔地存起来。",
      lead: "DreamJar 帮助父母和孩子把目标变成金币进度、备注、照片和回忆；不需要自建账号，也不依赖自定义服务器。",
      status: "iOS 即将上线",
      appStoreSoon: "App Store 链接准备中",
      proofPoints: ["本地优先记录", "私有 iCloud 同步", "成长日记", "Apple 内购"],
      featuresTitle: "DreamJar 能做什么",
      featuresLead: "用一个简单循环串起目标、努力、回忆和回顾。",
      features: [
        { title: "用金币推进梦想罐", text: "创建梦想、设置目标金币、存入或扣除金币，并看到梦想什么时候可以兑现。" },
        { title: "带照片的成长日记", text: "用时间线或日历记录家庭里的小瞬间，每篇日记最多支持三张照片。" },
        { title: "兑现后的纪念归档", text: "梦想兑现后，保留完整旅程、家长备注和照片，成为可以回看的纪念。" },
        { title: "从架构上更私密", text: "记录保存在设备上，在系统可用时通过用户自己的私有 iCloud 账号同步。" }
      ],
      privacyTitle: "没有自建账号系统",
      privacyLead: "DreamJar 围绕本地存储、用户自己的 iCloud 同步和 Apple 购买系统设计，而不是自定义后端。",
      proTitle: "先免费使用，需要时再升级 Pro",
      proLead: "免费版支持 1 个孩子和 3 个进行中的梦想罐。Pro 解锁多个孩子、无限梦想罐、年度回顾和成长回顾分享。",
      ctaTitle: "关注 DreamJar",
      ctaLead: "iOS 应用正在准备中。你可以在 X 关注 MuzoLab，获取上线和产品更新。",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "进行中的梦想",
        coins: "金币",
        journal: "日记",
        dreamName: "太空望远镜",
        progress: "145 / 200 金币",
        note: "阅读时间 +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJar 隐私政策 | MuzoLab",
      privacyMetaDescription: "DreamJar 隐私政策，说明本地存储、私有 iCloud 同步、照片、通知和 Apple 内购如何工作。",
      termsMetaTitle: "DreamJar 服务条款 | MuzoLab",
      termsMetaDescription: "DreamJar 服务条款，涵盖应用使用、家庭记录、金币规则、Pro 权益、iCloud 同步和 Apple 内购。",
      updated: `最后更新：${siteConfig.buildDate}`,
      privacyTitle: "DreamJar 隐私政策",
      privacyLead: "DreamJar 是一个本地优先的家庭应用。本政策说明应用会存储什么，以及同步和购买如何工作。",
      privacySections: [
        { title: "应用存储的数据", body: ["DreamJar 会存储孩子档案、梦想罐、金币记录、成长日记、照片、兑现备注、应用设置和购买权益状态。", "这些数据用于提供 DreamJar 的核心体验。"] },
        { title: "本地优先与 iCloud 同步", body: ["DreamJar 将记录保存在用户设备上。iCloud 可用时，记录可能通过用户自己的私有 iCloud 账号同步。", "MuzoLab 不运营自定义 DreamJar 账号系统，也不运营自定义同步服务器。"] },
        { title: "照片和媒体", body: ["用户选择的日记照片、头像、封面和兑现照片会作为应用数据保存在设备上，并可能根据系统设置通过 iCloud 同步。", "DreamJar 不会将这些照片用于广告。"] },
        { title: "购买和通知", body: ["DreamJar Pro 通过 Apple 内购处理，购买和订阅交易由 Apple 处理。", "用户允许通知后，应用会使用本地通知提醒梦想进度、目标日期、7 天检查和月度回顾。"] },
        { title: "联系", body: [`如有问题，可在 X 联系 MuzoLab：${siteConfig.xHandle}。`] }
      ],
      termsTitle: "DreamJar 服务条款",
      termsLead: "这些条款说明 DreamJar 的使用方式，以及免费功能和 Pro 功能之间的关系。",
      termsSections: [
        { title: "DreamJar 的用途", body: ["DreamJar 是一个家庭效率和回忆记录应用。它不是银行、钱包、支付服务或金融账户。", "DreamJar 里的金币只是象征性的进度单位，没有现金价值。"] },
        { title: "免费与 Pro 功能", body: ["免费用户可以创建 1 个孩子档案和最多 3 个进行中的梦想罐。", "DreamJar Pro 解锁多个孩子、无限进行中的梦想罐、年度回顾和成长回顾分享。Pro 到期后，已有记录不会被删除。"] },
        { title: "iCloud 与设备责任", body: ["DreamJar 使用设备本地存储，并可能通过用户自己的私有 iCloud 账号同步。可用性取决于 Apple 服务、设备设置、存储空间和网络状态。", "用户需要自行保护 Apple ID、设备和 iCloud 设置的安全。"] },
        { title: "购买", body: ["订阅和终身解锁通过 Apple 内购提供。支付、续订、取消、退款和订阅管理适用 Apple 的相关条款。", "产品价格和可用性可能因地区而异，并以 App Store 显示为准。"] },
        { title: "联系", body: [`支持和问题可在 X 联系 MuzoLab：${siteConfig.xHandle}。`] }
      ]
    },
    notFound: { title: "页面不存在", text: "这个页面还没有创建。" }
  },
  "zh-TW": {
    nav: {
      projects: "專案",
      contact: "在 X 聯絡",
      privacy: "隱私",
      terms: "條款",
      language: "語言",
      comingSoon: "即將上線",
      learnMore: "了解更多",
      backHome: "返回 MuzoLab",
      followOnX: "在 X 關注更新"
    },
    home: {
      metaTitle: "MuzoLab | 小而美的獨立產品工作室",
      metaDescription: "MuzoLab 是一個小而美的獨立工作室，專注打造克制、完整、長期可用的數位產品。了解 DreamJar 和未來專案。",
      eyebrow: "獨立產品工作室",
      headline: "小而美，克制且完整。",
      lead: "MuzoLab 打造聚焦的數位產品：介面安靜、隱私邊界清楚、功能不貪多，但每一處都盡量做完整。",
      projectSectionTitle: "專案",
      projectSectionLead: "每個產品都有自己的 landing page，也有一個可以長期維護的家。",
      principlesTitle: "MuzoLab 的產品原則",
      principlesLead: "更重視清晰、克制、隱私，以及把一個真實問題解決好。",
      principles: [
        { title: "從小處開始", text: "每個產品先承諾一件具體的事，再讓真正有用的功能留下來。" },
        { title: "預設尊重隱私", text: "只要產品允許，個人資料就應盡量留在本地或使用者自己擁有的系統裡。" },
        { title: "面向長期使用", text: "選擇耐看的介面、清楚的語言和可維護的工程實作。" }
      ],
      dreamJarCard: {
        label: "iOS 應用",
        title: "DreamJar",
        text: "一個親子夢想罐與成長日記，把每天的小努力變成看得見的進度。"
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | 親子夢想罐與成長日記",
      metaDescription: "DreamJar 是 MuzoLab 即將推出的 iOS 應用，支援親子夢想罐、金幣記錄、成長日記、iCloud 私有同步和本地優先記錄。",
      eyebrow: "iOS 即將上線",
      headline: "讓一家人的夢想，被溫柔地存起來。",
      lead: "DreamJar 幫助父母和孩子把目標變成金幣進度、備註、照片和回憶；不需要自建帳號，也不依賴自訂伺服器。",
      status: "iOS 即將上線",
      appStoreSoon: "App Store 連結準備中",
      proofPoints: ["本地優先記錄", "私有 iCloud 同步", "成長日記", "Apple 內購"],
      featuresTitle: "DreamJar 能做什麼",
      featuresLead: "用一個簡單循環串起目標、努力、回憶和回顧。",
      features: [
        { title: "用金幣推進夢想罐", text: "建立夢想、設定目標金幣、存入或扣除金幣，並看到夢想什麼時候可以兌現。" },
        { title: "帶照片的成長日記", text: "用時間線或日曆記錄家庭裡的小瞬間，每篇日記最多支援三張照片。" },
        { title: "兌現後的紀念歸檔", text: "夢想兌現後，保留完整旅程、家長備註和照片，成為可以回看的紀念。" },
        { title: "從架構上更私密", text: "記錄保存在裝置上，在系統可用時透過使用者自己的私有 iCloud 帳號同步。" }
      ],
      privacyTitle: "沒有自建帳號系統",
      privacyLead: "DreamJar 圍繞本地儲存、使用者自己的 iCloud 同步和 Apple 購買系統設計，而不是自訂後端。",
      proTitle: "先免費使用，需要時再升級 Pro",
      proLead: "免費版支援 1 個孩子和 3 個進行中的夢想罐。Pro 解鎖多個孩子、無限夢想罐、年度回顧和成長回顧分享。",
      ctaTitle: "關注 DreamJar",
      ctaLead: "iOS 應用正在準備中。你可以在 X 關注 MuzoLab，取得上線和產品更新。",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "進行中的夢想",
        coins: "金幣",
        journal: "日記",
        dreamName: "太空望遠鏡",
        progress: "145 / 200 金幣",
        note: "閱讀時間 +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJar 隱私政策 | MuzoLab",
      privacyMetaDescription: "DreamJar 隱私政策，說明本地儲存、私有 iCloud 同步、照片、通知和 Apple 內購如何運作。",
      termsMetaTitle: "DreamJar 服務條款 | MuzoLab",
      termsMetaDescription: "DreamJar 服務條款，涵蓋應用使用、家庭記錄、金幣規則、Pro 權益、iCloud 同步和 Apple 內購。",
      updated: `最後更新：${siteConfig.buildDate}`,
      privacyTitle: "DreamJar 隱私政策",
      privacyLead: "DreamJar 是一個本地優先的家庭應用。本政策說明應用會儲存什麼，以及同步和購買如何運作。",
      privacySections: [
        { title: "應用儲存的資料", body: ["DreamJar 會儲存孩子檔案、夢想罐、金幣記錄、成長日記、照片、兌現備註、應用設定和購買權益狀態。"] },
        { title: "本地優先與 iCloud 同步", body: ["DreamJar 將記錄保存在使用者裝置上。iCloud 可用時，記錄可能透過使用者自己的私有 iCloud 帳號同步。MuzoLab 不營運自訂 DreamJar 帳號系統或同步伺服器。"] },
        { title: "照片和媒體", body: ["使用者選擇的照片會作為應用資料保存在裝置上，並可能依系統設定透過 iCloud 同步。DreamJar 不會將這些照片用於廣告。"] },
        { title: "購買和通知", body: ["DreamJar Pro 透過 Apple 內購處理。使用者允許通知後，應用會使用本地通知提醒夢想進度、目標日期、檢查和月度回顧。"] },
        { title: "聯絡", body: [`如有問題，可在 X 聯絡 MuzoLab：${siteConfig.xHandle}。`] }
      ],
      termsTitle: "DreamJar 服務條款",
      termsLead: "這些條款說明 DreamJar 的使用方式，以及免費功能和 Pro 功能之間的關係。",
      termsSections: [
        { title: "DreamJar 的用途", body: ["DreamJar 是一個家庭效率和回憶記錄應用。它不是銀行、錢包、支付服務或金融帳戶。DreamJar 裡的金幣只是象徵性的進度單位，沒有現金價值。"] },
        { title: "免費與 Pro 功能", body: ["免費使用者可以建立 1 個孩子檔案和最多 3 個進行中的夢想罐。Pro 解鎖多個孩子、無限夢想罐、年度回顧和成長回顧分享。Pro 到期後，已有記錄不會被刪除。"] },
        { title: "iCloud 與裝置責任", body: ["DreamJar 使用裝置本地儲存，並可能透過使用者自己的私有 iCloud 帳號同步。可用性取決於 Apple 服務、裝置設定、儲存空間和網路狀態。"] },
        { title: "購買", body: ["訂閱和終身解鎖透過 Apple 內購提供。支付、續訂、取消、退款和訂閱管理適用 Apple 的相關條款。"] },
        { title: "聯絡", body: [`支援和問題可在 X 聯絡 MuzoLab：${siteConfig.xHandle}。`] }
      ]
    },
    notFound: { title: "頁面不存在", text: "這個頁面還沒有建立。" }
  },
  de: {
    nav: {
      projects: "Projekte",
      contact: "Kontakt auf X",
      privacy: "Datenschutz",
      terms: "Bedingungen",
      language: "Sprache",
      comingSoon: "Demnächst",
      learnMore: "Mehr erfahren",
      backHome: "Zurück zu MuzoLab",
      followOnX: "Updates auf X folgen"
    },
    home: {
      metaTitle: "MuzoLab | Kleines unabhängiges Produktstudio",
      metaDescription:
        "MuzoLab ist ein kleines unabhängiges Studio für durchdachte, vollständige digitale Produkte. Entdecke DreamJar und kommende kleine, schöne Projekte.",
      eyebrow: "Unabhängiges Produktstudio",
      headline: "Kleine, durchdachte, vollständige Produkte.",
      lead:
        "MuzoLab baut fokussierte digitale Produkte mit ruhiger Oberfläche, klarer Datenschutzhaltung und genug Sorgfalt, damit sie fertig wirken.",
      projectSectionTitle: "Projekte",
      projectSectionLead: "Eine wachsende Sammlung kleiner Produkte, jedes mit eigener Landingpage und langfristigem Zuhause.",
      principlesTitle: "Wie MuzoLab baut",
      principlesLead: "Das Studio bevorzugt Klarheit, Zurückhaltung, Datenschutz und Produkte, die eine echte Aufgabe gut lösen.",
      principles: [
        { title: "Bewusst klein", text: "Jedes Produkt beginnt mit einem engen Versprechen und verdient jede Funktion durch Nutzen." },
        { title: "Datenschutz als Standard", text: "Persönliche Daten bleiben möglichst lokal oder in Systemen, die dem Nutzer gehören." },
        { title: "Für lange Nutzung", text: "MuzoLab bevorzugt langlebige Oberflächen, klare Sprache und wartbare Technik." }
      ],
      dreamJarCard: {
        label: "iOS-App",
        title: "DreamJar",
        text: "Ein Traumglas und Wachstumstagebuch für Eltern und Kinder, das tägliche Mühe sichtbar macht."
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | Traumgläser und Wachstumstagebuch für Familien",
      metaDescription:
        "DreamJar ist eine kommende iOS-App von MuzoLab für Traumgläser, Münzverläufe, Wachstumstagebücher, iCloud-Sync und lokale private Aufzeichnungen.",
      eyebrow: "Demnächst für iOS",
      headline: "Ein wärmerer Weg, Familienträume zu sammeln.",
      lead:
        "DreamJar hilft Eltern und Kindern, Ziele als Münzfortschritt, Notizen, Fotos und Erinnerungen festzuhalten, ohne eigenes Konto oder eigenen Server.",
      status: "Demnächst für iOS",
      appStoreSoon: "App-Store-Link folgt",
      proofPoints: ["Local-first", "Privater iCloud-Sync", "Wachstumstagebuch", "Apple In-App-Kauf"],
      featuresTitle: "Was DreamJar kann",
      featuresLead: "Ein einfacher Ablauf für Ziele, Mühe, Erinnerungen und Rückblicke.",
      features: [
        { title: "Traumgläser mit Münzfortschritt", text: "Träume erstellen, Zielmünzen setzen, Münzen einzahlen oder abziehen und die Erfüllung sehen." },
        { title: "Wachstumstagebuch mit Fotos", text: "Kleine Familienmomente in Timeline oder Kalender festhalten, mit bis zu drei Fotos pro Eintrag." },
        { title: "Erfüllte Erinnerungen", text: "Erfüllte Träume bleiben mit Verlauf, Elternnotizen und Fotos im Archiv." },
        { title: "Privat durch Architektur", text: "Daten liegen auf dem Gerät und synchronisieren bei Verfügbarkeit über das private iCloud-Konto." }
      ],
      privacyTitle: "Kein eigenes Kontosystem",
      privacyLead: "DreamJar setzt auf lokale Speicherung, nutzergesteuerten iCloud-Sync und Apples Kaufsystem statt auf ein eigenes Backend.",
      proTitle: "Kostenlos starten, Pro wenn die Familie wächst",
      proLead: "Kostenlos: ein Kind und drei aktive Traumgläser. Pro schaltet mehrere Kinder, unbegrenzte Gläser, Jahresrückblicke und Teilen frei.",
      ctaTitle: "DreamJar folgen",
      ctaLead: "Die iOS-App wird vorbereitet. Folge MuzoLab auf X für Release- und Produktupdates.",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "Aktive Träume",
        coins: "Münzen",
        journal: "Tagebuch",
        dreamName: "Weltraumteleskop",
        progress: "145 / 200 Münzen",
        note: "Lesezeit +5"
      }
    },
    legal: {
      privacyMetaTitle: "DreamJar Datenschutzrichtlinie | MuzoLab",
      privacyMetaDescription: "Datenschutzrichtlinie zu lokaler Speicherung, privatem iCloud-Sync, Fotos, Benachrichtigungen und Apple In-App-Käufen in DreamJar.",
      termsMetaTitle: "DreamJar Nutzungsbedingungen | MuzoLab",
      termsMetaDescription: "Nutzungsbedingungen zu App-Nutzung, Familienaufzeichnungen, Münzregeln, Pro-Zugang, iCloud-Sync und Apple In-App-Käufen.",
      updated: `Zuletzt aktualisiert: ${siteConfig.buildDate}`,
      privacyTitle: "DreamJar Datenschutzrichtlinie",
      privacyLead: "DreamJar ist als local-first Familien-App konzipiert.",
      privacySections: [
        { title: "Gespeicherte Daten", body: ["DreamJar speichert Kinderprofile, Traumgläser, Münztransaktionen, Tagebucheinträge, Fotos, Erfüllungsnotizen, Einstellungen und Kaufberechtigungen."] },
        { title: "Lokal und iCloud", body: ["Aufzeichnungen werden auf dem Gerät gespeichert und können über das private iCloud-Konto synchronisiert werden. MuzoLab betreibt kein eigenes DreamJar-Konto oder Sync-Server."] },
        { title: "Fotos und Medien", body: ["Ausgewählte Fotos werden als App-Daten gespeichert und können je nach Systemeinstellung über iCloud synchronisiert werden. Sie werden nicht für Werbung genutzt."] },
        { title: "Käufe und Benachrichtigungen", body: ["DreamJar Pro wird über Apple In-App-Kauf abgewickelt. Benachrichtigungen erinnern nur bei Erlaubnis an Fortschritt, Termine, Check-ins und Rückblicke."] },
        { title: "Kontakt", body: [`Fragen an MuzoLab auf X: ${siteConfig.xHandle}.`] }
      ],
      termsTitle: "DreamJar Nutzungsbedingungen",
      termsLead: "Diese Bedingungen beschreiben die Nutzung von DreamJar und das Verhältnis von kostenlosen und Pro-Funktionen.",
      termsSections: [
        { title: "Nutzung", body: ["DreamJar ist eine Familien- und Erinnerungs-App. Sie ist keine Bank, Wallet, Zahlungsdienst oder Finanzkonto. Münzen haben keinen Barwert."] },
        { title: "Free und Pro", body: ["Kostenlos können ein Kind und bis zu drei aktive Traumgläser erstellt werden. Pro schaltet mehrere Kinder, unbegrenzte Gläser, Jahresrückblicke und Teilen frei. Bestehende Daten werden bei Ablauf nicht gelöscht."] },
        { title: "iCloud und Geräte", body: ["Verfügbarkeit hängt von Apple-Diensten, Geräteeinstellungen, Speicher und Netzwerk ab. Nutzer sind für Apple ID, Geräte und iCloud-Sicherheit verantwortlich."] },
        { title: "Käufe", body: ["Abos und Lifetime-Freischaltungen laufen über Apple In-App-Kauf. Für Zahlung, Verlängerung, Kündigung und Erstattung gelten Apples Bedingungen."] },
        { title: "Kontakt", body: [`Support über X: ${siteConfig.xHandle}.`] }
      ]
    },
    notFound: { title: "Seite nicht gefunden", text: "Diese Seite existiert noch nicht." }
  },
  fr: {
    nav: {
      projects: "Projets",
      contact: "Contact sur X",
      privacy: "Confidentialité",
      terms: "Conditions",
      language: "Langue",
      comingSoon: "Bientôt",
      learnMore: "En savoir plus",
      backHome: "Retour à MuzoLab",
      followOnX: "Suivre sur X"
    },
    home: {
      metaTitle: "MuzoLab | Petit studio produit indépendant",
      metaDescription:
        "MuzoLab est un petit studio indépendant qui crée des produits numériques réfléchis et complets. Découvrez DreamJar et les futurs petits beaux projets.",
      eyebrow: "Studio produit indépendant",
      headline: "Des produits petits, réfléchis et complets.",
      lead:
        "MuzoLab conçoit des produits numériques ciblés, avec une interface calme, une posture claire sur la vie privée et un niveau de soin qui donne une vraie sensation de finition.",
      projectSectionTitle: "Projets",
      projectSectionLead: "Une collection de petits produits, chacun avec sa landing page et une maison durable.",
      principlesTitle: "La façon MuzoLab",
      principlesLead: "Clarté, retenue, vie privée et résolution soignée d’un vrai besoin.",
      principles: [
        { title: "Petit par design", text: "Chaque produit commence par une promesse étroite et ne garde que les fonctions utiles." },
        { title: "Vie privée par défaut", text: "Quand c’est possible, les données personnelles restent locales ou dans des systèmes appartenant à l’utilisateur." },
        { title: "Fait pour durer", text: "MuzoLab choisit des interfaces durables, un langage clair et une ingénierie maintenable." }
      ],
      dreamJarCard: {
        label: "App iOS",
        title: "DreamJar",
        text: "Un bocal à rêves et journal de croissance parent-enfant pour rendre les efforts quotidiens visibles."
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | Bocaux à rêves et journal de croissance",
      metaDescription:
        "DreamJar est une future app iOS de MuzoLab pour bocaux à rêves familiaux, suivi de pièces, journal de croissance, synchronisation iCloud et données locales privées.",
      eyebrow: "Bientôt sur iOS",
      headline: "Une façon plus douce d’épargner pour les rêves en famille.",
      lead:
        "DreamJar aide parents et enfants à transformer des objectifs en progression, notes, photos et souvenirs sans compte personnalisé ni serveur dédié.",
      status: "Bientôt sur iOS",
      appStoreSoon: "Lien App Store bientôt disponible",
      proofPoints: ["Local-first", "Synchronisation iCloud privée", "Journal de croissance", "Achat intégré Apple"],
      featuresTitle: "Ce que fait DreamJar",
      featuresLead: "Une boucle simple pour objectifs, efforts, souvenirs et bilans.",
      features: [
        { title: "Bocaux à rêves avec pièces", text: "Créez des rêves, fixez des objectifs, ajoutez ou retirez des pièces et voyez quand un rêve est prêt." },
        { title: "Journal de croissance avec photos", text: "Capturez les petits moments familiaux en timeline ou calendrier, avec jusqu’à trois photos par entrée." },
        { title: "Souvenirs de réalisation", text: "Une fois un rêve réalisé, gardez le parcours, les notes parentales et les photos dans une archive." },
        { title: "Privé par architecture", text: "Les données sont stockées sur l’appareil et synchronisées via le compte iCloud privé de l’utilisateur si disponible." }
      ],
      privacyTitle: "Pas de compte personnalisé",
      privacyLead: "DreamJar repose sur le stockage local, iCloud contrôlé par l’utilisateur et le système d’achat Apple plutôt qu’un backend maison.",
      proTitle: "Gratuit d’abord, Pro quand la famille grandit",
      proLead: "Le gratuit prend en charge un enfant et trois rêves actifs. Pro débloque plusieurs enfants, des rêves illimités, les bilans annuels et le partage.",
      ctaTitle: "Suivre DreamJar",
      ctaLead: "L’app iOS est en préparation. Suivez MuzoLab sur X pour les nouvelles de lancement.",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "Rêves actifs",
        coins: "Pièces",
        journal: "Journal",
        dreamName: "Télescope spatial",
        progress: "145 / 200 pièces",
        note: "Lecture +5"
      }
    },
    legal: {
      privacyMetaTitle: "Politique de confidentialité DreamJar | MuzoLab",
      privacyMetaDescription: "Politique de confidentialité DreamJar sur stockage local, iCloud privé, photos, notifications et achats intégrés Apple.",
      termsMetaTitle: "Conditions d’utilisation DreamJar | MuzoLab",
      termsMetaDescription: "Conditions sur l’utilisation de DreamJar, les dossiers familiaux, les règles de pièces, Pro, iCloud et les achats Apple.",
      updated: `Dernière mise à jour : ${siteConfig.buildDate}`,
      privacyTitle: "Politique de confidentialité DreamJar",
      privacyLead: "DreamJar est conçue comme une app familiale local-first.",
      privacySections: [
        { title: "Données stockées", body: ["DreamJar stocke profils d’enfants, bocaux, transactions, journaux, photos, notes, réglages et état d’achat."] },
        { title: "Local-first et iCloud", body: ["Les données sont stockées sur l’appareil et peuvent se synchroniser via le compte iCloud privé. MuzoLab n’exploite pas de compte DreamJar ni de serveur de synchronisation maison."] },
        { title: "Photos et médias", body: ["Les photos choisies sont stockées comme données de l’app et peuvent être synchronisées par iCloud selon les réglages système. Elles ne sont pas utilisées pour la publicité."] },
        { title: "Achats et notifications", body: ["DreamJar Pro passe par les achats intégrés Apple. Les notifications locales sont utilisées seulement avec l’autorisation de l’utilisateur."] },
        { title: "Contact", body: [`Questions : ${siteConfig.xHandle} sur X.`] }
      ],
      termsTitle: "Conditions d’utilisation DreamJar",
      termsLead: "Ces conditions décrivent l’utilisation de DreamJar et le lien entre les fonctions gratuites et Pro.",
      termsSections: [
        { title: "Utilisation", body: ["DreamJar est une app familiale de productivité et de souvenirs. Ce n’est pas une banque, un portefeuille ou un service de paiement. Les pièces n’ont pas de valeur monétaire."] },
        { title: "Gratuit et Pro", body: ["Le gratuit permet un enfant et trois rêves actifs. Pro débloque plusieurs enfants, des rêves illimités, les bilans annuels et le partage. Les données existantes ne sont pas supprimées si Pro prend fin."] },
        { title: "iCloud et appareil", body: ["La disponibilité dépend des services Apple, réglages, stockage et réseau. L’utilisateur protège son Apple ID, ses appareils et iCloud."] },
        { title: "Achats", body: ["Les abonnements et achats à vie sont fournis via Apple. Paiement, renouvellement, annulation et remboursement suivent les conditions Apple."] },
        { title: "Contact", body: [`Support : ${siteConfig.xHandle} sur X.`] }
      ]
    },
    notFound: { title: "Page introuvable", text: "Cette page n’existe pas encore." }
  },
  es: {
    nav: {
      projects: "Proyectos",
      contact: "Contactar en X",
      privacy: "Privacidad",
      terms: "Términos",
      language: "Idioma",
      comingSoon: "Próximamente",
      learnMore: "Más información",
      backHome: "Volver a MuzoLab",
      followOnX: "Seguir en X"
    },
    home: {
      metaTitle: "MuzoLab | Pequeño estudio independiente de productos",
      metaDescription:
        "MuzoLab es un pequeño estudio independiente que crea productos digitales pensados y completos. Explora DreamJar y futuros proyectos pequeños y bellos.",
      eyebrow: "Estudio independiente de productos",
      headline: "Productos pequeños, pensados y completos.",
      lead:
        "MuzoLab crea productos digitales enfocados, con interfaz tranquila, postura clara de privacidad y suficiente oficio para sentirse terminados.",
      projectSectionTitle: "Proyectos",
      projectSectionLead: "Una colección creciente de productos pequeños, cada uno con su propia landing page y un hogar duradero.",
      principlesTitle: "Cómo construye MuzoLab",
      principlesLead: "Claridad, contención, privacidad y resolver bien una tarea real.",
      principles: [
        { title: "Pequeño por diseño", text: "Cada producto empieza con una promesa estrecha y cada función debe ganarse su lugar." },
        { title: "Privacidad por defecto", text: "Cuando es posible, los datos personales permanecen locales o en sistemas propiedad del usuario." },
        { title: "Hecho para durar", text: "MuzoLab prefiere superficies duraderas, lenguaje claro e ingeniería mantenible." }
      ],
      dreamJarCard: {
        label: "App iOS",
        title: "DreamJar",
        text: "Un frasco de sueños y diario de crecimiento para padres e hijos que hace visible el esfuerzo diario."
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | Frascos de sueños y diario de crecimiento",
      metaDescription:
        "DreamJar es una próxima app iOS de MuzoLab para frascos de sueños familiares, registro de monedas, diario de crecimiento, iCloud privado y datos locales.",
      eyebrow: "Próximamente para iOS",
      headline: "Una forma más cálida de ahorrar para los sueños familiares.",
      lead:
        "DreamJar ayuda a padres e hijos a convertir metas en progreso con monedas, notas, fotos y recuerdos sin cuenta propia ni servidor personalizado.",
      status: "Próximamente para iOS",
      appStoreSoon: "Enlace de App Store próximamente",
      proofPoints: ["Local-first", "Sincronización iCloud privada", "Diario de crecimiento", "Compra integrada de Apple"],
      featuresTitle: "Qué hace DreamJar",
      featuresLead: "Un ciclo simple para metas, esfuerzo, recuerdos y revisiones.",
      features: [
        { title: "Frascos con progreso de monedas", text: "Crea sueños, define monedas objetivo, suma o resta monedas y mira cuándo se pueden cumplir." },
        { title: "Diario con fotos", text: "Guarda pequeños momentos familiares en línea de tiempo o calendario, con hasta tres fotos por entrada." },
        { title: "Recuerdos cumplidos", text: "Cuando un sueño se cumple, conserva el viaje, notas de padres y fotos en un archivo." },
        { title: "Privado por arquitectura", text: "Los registros se guardan en el dispositivo y se sincronizan por la cuenta privada de iCloud cuando está disponible." }
      ],
      privacyTitle: "Sin sistema de cuentas propio",
      privacyLead: "DreamJar se diseña con almacenamiento local, iCloud controlado por el usuario y compras de Apple en lugar de un backend propio.",
      proTitle: "Gratis primero, Pro cuando la familia crece",
      proLead: "El plan gratuito admite un hijo y tres sueños activos. Pro desbloquea varios hijos, sueños ilimitados, revisión anual y compartir revisiones.",
      ctaTitle: "Seguir DreamJar",
      ctaLead: "La app iOS está en preparación. Sigue a MuzoLab en X para novedades.",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "Sueños activos",
        coins: "Monedas",
        journal: "Diario",
        dreamName: "Telescopio espacial",
        progress: "145 / 200 monedas",
        note: "Lectura +5"
      }
    },
    legal: {
      privacyMetaTitle: "Política de privacidad de DreamJar | MuzoLab",
      privacyMetaDescription: "Política de privacidad de DreamJar sobre almacenamiento local, iCloud privado, fotos, notificaciones y compras integradas de Apple.",
      termsMetaTitle: "Términos de servicio de DreamJar | MuzoLab",
      termsMetaDescription: "Términos sobre uso de DreamJar, registros familiares, reglas de monedas, Pro, iCloud y compras integradas de Apple.",
      updated: `Última actualización: ${siteConfig.buildDate}`,
      privacyTitle: "Política de privacidad de DreamJar",
      privacyLead: "DreamJar está diseñada como una app familiar local-first.",
      privacySections: [
        { title: "Datos guardados", body: ["DreamJar guarda perfiles de hijos, frascos, transacciones, entradas, fotos, notas, ajustes y estado de compra."] },
        { title: "Local-first e iCloud", body: ["Los registros se guardan en el dispositivo y pueden sincronizarse por la cuenta privada de iCloud. MuzoLab no opera cuentas DreamJar ni servidor propio de sincronización."] },
        { title: "Fotos y medios", body: ["Las fotos elegidas se guardan como datos de la app y pueden sincronizarse por iCloud según la configuración del sistema. No se usan para publicidad."] },
        { title: "Compras y notificaciones", body: ["DreamJar Pro se procesa mediante compras integradas de Apple. Las notificaciones locales se usan solo si el usuario las permite."] },
        { title: "Contacto", body: [`Preguntas en X: ${siteConfig.xHandle}.`] }
      ],
      termsTitle: "Términos de servicio de DreamJar",
      termsLead: "Estos términos describen el uso de DreamJar y la relación entre funciones gratuitas y Pro.",
      termsSections: [
        { title: "Uso", body: ["DreamJar es una app familiar de productividad y recuerdos. No es banco, monedero ni servicio de pago. Las monedas no tienen valor en efectivo."] },
        { title: "Gratis y Pro", body: ["Gratis permite un hijo y tres sueños activos. Pro desbloquea varios hijos, sueños ilimitados, revisión anual y compartir. Los datos existentes no se borran si Pro termina."] },
        { title: "iCloud y dispositivo", body: ["La disponibilidad depende de servicios Apple, ajustes, almacenamiento y red. El usuario protege su Apple ID, dispositivos e iCloud."] },
        { title: "Compras", body: ["Suscripciones y desbloqueos de por vida se ofrecen mediante Apple. Pagos, renovaciones, cancelaciones y reembolsos siguen los términos de Apple."] },
        { title: "Contacto", body: [`Soporte en X: ${siteConfig.xHandle}.`] }
      ]
    },
    notFound: { title: "Página no encontrada", text: "Esta página aún no existe." }
  },
  th: {
    nav: {
      projects: "โปรเจกต์",
      contact: "ติดต่อทาง X",
      privacy: "ความเป็นส่วนตัว",
      terms: "ข้อกำหนด",
      language: "ภาษา",
      comingSoon: "เร็ว ๆ นี้",
      learnMore: "ดูเพิ่มเติม",
      backHome: "กลับสู่ MuzoLab",
      followOnX: "ติดตามบน X"
    },
    home: {
      metaTitle: "MuzoLab | สตูดิโอผลิตภัณฑ์อิสระขนาดเล็ก",
      metaDescription:
        "MuzoLab คือสตูดิโออิสระขนาดเล็กที่สร้างผลิตภัณฑ์ดิจิทัลที่คิดมาอย่างดีและสมบูรณ์ สำรวจ DreamJar และโปรเจกต์เล็กแต่สวยงามในอนาคต",
      eyebrow: "สตูดิโอผลิตภัณฑ์อิสระ",
      headline: "ผลิตภัณฑ์เล็ก รอบคอบ และสมบูรณ์",
      lead:
        "MuzoLab สร้างผลิตภัณฑ์ดิจิทัลที่โฟกัส มีอินเทอร์เฟซสงบ เคารพความเป็นส่วนตัว และให้ความรู้สึกว่าใช้งานได้ครบจริง",
      projectSectionTitle: "โปรเจกต์",
      projectSectionLead: "คอลเลกชันของผลิตภัณฑ์ขนาดเล็ก แต่ละตัวมี landing page และบ้านระยะยาวของตัวเอง",
      principlesTitle: "วิธีสร้างของ MuzoLab",
      principlesLead: "ให้ความสำคัญกับความชัดเจน ความพอดี ความเป็นส่วนตัว และการแก้ปัญหาจริงให้ดี",
      principles: [
        { title: "เล็กโดยตั้งใจ", text: "เริ่มจากคำสัญญาที่แคบ แล้วเพิ่มเฉพาะฟีเจอร์ที่มีประโยชน์จริง" },
        { title: "ความเป็นส่วนตัวเป็นค่าเริ่มต้น", text: "เมื่อทำได้ ข้อมูลส่วนตัวควรอยู่ในเครื่องหรือระบบที่ผู้ใช้เป็นเจ้าของ" },
        { title: "สร้างให้ใช้ได้นาน", text: "เลือกหน้าจอที่ทนเวลา ภาษาที่เข้าใจง่าย และวิศวกรรมที่ดูแลต่อได้" }
      ],
      dreamJarCard: {
        label: "แอป iOS",
        title: "DreamJar",
        text: "กระปุกความฝันและบันทึกการเติบโตของพ่อแม่กับเด็ก ที่ทำให้ความพยายามในแต่ละวันมองเห็นได้"
      }
    },
    dreamjar: {
      metaTitle: "DreamJar | กระปุกความฝันและบันทึกการเติบโตของครอบครัว",
      metaDescription:
        "DreamJar คือแอป iOS จาก MuzoLab สำหรับกระปุกความฝันของครอบครัว บันทึกเหรียญ สมุดบันทึกการเติบโต iCloud ส่วนตัว และข้อมูลแบบ local-first",
      eyebrow: "เร็ว ๆ นี้บน iOS",
      headline: "วิธีที่อบอุ่นกว่าในการเก็บความฝันของครอบครัว",
      lead:
        "DreamJar ช่วยให้พ่อแม่และเด็กเปลี่ยนเป้าหมายเป็นความคืบหน้าด้วยเหรียญ โน้ต รูปภาพ และความทรงจำ โดยไม่ต้องมีบัญชีหรือเซิร์ฟเวอร์เฉพาะ",
      status: "เร็ว ๆ นี้บน iOS",
      appStoreSoon: "ลิงก์ App Store กำลังเตรียม",
      proofPoints: ["ข้อมูล local-first", "ซิงค์ iCloud ส่วนตัว", "บันทึกการเติบโต", "ซื้อผ่าน Apple"],
      featuresTitle: "DreamJar ทำอะไรได้บ้าง",
      featuresLead: "ลูปง่าย ๆ สำหรับเป้าหมาย ความพยายาม ความทรงจำ และการทบทวน",
      features: [
        { title: "กระปุกความฝันพร้อมเหรียญ", text: "สร้างความฝัน ตั้งเป้าเหรียญ เพิ่มหรือลดเหรียญ และดูว่าเมื่อไรพร้อมทำให้เป็นจริง" },
        { title: "บันทึกการเติบโตพร้อมรูป", text: "เก็บช่วงเวลาเล็ก ๆ ของครอบครัวในไทม์ไลน์หรือปฏิทิน พร้อมรูปได้สูงสุดสามรูปต่อบันทึก" },
        { title: "ความทรงจำเมื่อทำสำเร็จ", text: "เมื่อความฝันสำเร็จ เก็บเส้นทาง โน้ตของผู้ปกครอง และรูปไว้ในคลัง" },
        { title: "เป็นส่วนตัวตั้งแต่โครงสร้าง", text: "ข้อมูลเก็บบนอุปกรณ์ และซิงค์ผ่านบัญชี iCloud ส่วนตัวของผู้ใช้เมื่อพร้อมใช้งาน" }
      ],
      privacyTitle: "ไม่มีระบบบัญชีของตัวเอง",
      privacyLead: "DreamJar ออกแบบบนการเก็บข้อมูลในเครื่อง การซิงค์ iCloud ของผู้ใช้ และระบบซื้อของ Apple แทน backend เฉพาะ",
      proTitle: "เริ่มใช้ฟรี แล้วค่อยใช้ Pro เมื่อครอบครัวเติบโต",
      proLead: "แผนฟรีรองรับเด็ก 1 คนและกระปุกความฝันที่กำลังใช้งาน 3 ใบ Pro ปลดล็อกเด็กหลายคน กระปุกไม่จำกัด รีวิวรายปี และการแชร์รีวิว",
      ctaTitle: "ติดตาม DreamJar",
      ctaLead: "แอป iOS กำลังเตรียมเปิดตัว ติดตาม MuzoLab บน X เพื่อรับข่าวอัปเดต",
      phone: {
        child: "Little Dreamer",
        plan: "Free",
        activeDreams: "ความฝันที่กำลังทำ",
        coins: "เหรียญ",
        journal: "บันทึก",
        dreamName: "กล้องโทรทรรศน์อวกาศ",
        progress: "145 / 200 เหรียญ",
        note: "เวลาอ่านหนังสือ +5"
      }
    },
    legal: {
      privacyMetaTitle: "นโยบายความเป็นส่วนตัว DreamJar | MuzoLab",
      privacyMetaDescription: "นโยบายความเป็นส่วนตัวของ DreamJar เกี่ยวกับการเก็บข้อมูลในเครื่อง iCloud ส่วนตัว รูปภาพ การแจ้งเตือน และการซื้อผ่าน Apple",
      termsMetaTitle: "ข้อกำหนดการใช้บริการ DreamJar | MuzoLab",
      termsMetaDescription: "ข้อกำหนดเกี่ยวกับการใช้ DreamJar บันทึกครอบครัว กฎเหรียญ Pro การซิงค์ iCloud และการซื้อผ่าน Apple",
      updated: `อัปเดตล่าสุด: ${siteConfig.buildDate}`,
      privacyTitle: "นโยบายความเป็นส่วนตัว DreamJar",
      privacyLead: "DreamJar ออกแบบเป็นแอปครอบครัวแบบ local-first",
      privacySections: [
        { title: "ข้อมูลที่แอปเก็บ", body: ["DreamJar เก็บโปรไฟล์เด็ก กระปุกความฝัน รายการเหรียญ บันทึก รูปภาพ โน้ตการทำสำเร็จ การตั้งค่า และสถานะสิทธิ์การซื้อ"] },
        { title: "Local-first และ iCloud", body: ["ข้อมูลเก็บบนอุปกรณ์ และอาจซิงค์ผ่านบัญชี iCloud ส่วนตัวเมื่อ iCloud พร้อมใช้งาน MuzoLab ไม่ให้บริการบัญชี DreamJar หรือเซิร์ฟเวอร์ซิงค์เฉพาะ"] },
        { title: "รูปภาพและสื่อ", body: ["รูปที่เลือกจะถูกเก็บเป็นข้อมูลแอป และอาจซิงค์ผ่าน iCloud ตามการตั้งค่าระบบ DreamJar ไม่ใช้รูปเหล่านี้เพื่อโฆษณา"] },
        { title: "การซื้อและการแจ้งเตือน", body: ["DreamJar Pro จัดการผ่านการซื้อในแอปของ Apple การแจ้งเตือนในเครื่องใช้เมื่อผู้ใช้อนุญาตเท่านั้น"] },
        { title: "ติดต่อ", body: [`ส่งคำถามถึง MuzoLab บน X ได้ที่ ${siteConfig.xHandle}`] }
      ],
      termsTitle: "ข้อกำหนดการใช้บริการ DreamJar",
      termsLead: "ข้อกำหนดนี้อธิบายการใช้ DreamJar และความสัมพันธ์ระหว่างฟีเจอร์ฟรีกับ Pro",
      termsSections: [
        { title: "การใช้ DreamJar", body: ["DreamJar เป็นแอปครอบครัวสำหรับประสิทธิภาพและความทรงจำ ไม่ใช่ธนาคาร กระเป๋าเงิน บริการชำระเงิน หรือบัญชีการเงิน เหรียญในแอปไม่มีมูลค่าเงินสด"] },
        { title: "ฟรีและ Pro", body: ["ผู้ใช้ฟรีสร้างเด็กได้ 1 คนและกระปุกที่ใช้งานได้ 3 ใบ Pro ปลดล็อกเด็กหลายคน กระปุกไม่จำกัด รีวิวรายปี และการแชร์ ข้อมูลเดิมจะไม่ถูกลบเมื่อ Pro สิ้นสุด"] },
        { title: "iCloud และอุปกรณ์", body: ["ความพร้อมใช้งานขึ้นกับบริการ Apple การตั้งค่าอุปกรณ์ พื้นที่เก็บข้อมูล และเครือข่าย ผู้ใช้ต้องดูแล Apple ID อุปกรณ์ และ iCloud ของตนเอง"] },
        { title: "การซื้อ", body: ["การสมัครสมาชิกและการปลดล็อกตลอดชีพให้บริการผ่าน Apple การชำระเงิน ต่ออายุ ยกเลิก และคืนเงินเป็นไปตามข้อกำหนดของ Apple"] },
        { title: "ติดต่อ", body: [`ขอรับการสนับสนุนได้ทาง X: ${siteConfig.xHandle}`] }
      ]
    },
    notFound: { title: "ไม่พบหน้า", text: "หน้านี้ยังไม่มีอยู่" }
  }
};
